import React, { useState, useEffect } from 'react';
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { WEBSITE_NAV_ITEMS } from './websiteData';
import './WebsiteShell.css';

const PAGE_TITLES = {
  '/website': 'Lexint — Code Intelligence Platform',
  '/website/product/depguard': 'DepGuard — SBOM & Supply Chain Security | Lexint',
  '/website/product/vigil': 'Vigil — Open-Source SAST Engine | Lexint',
  '/website/product/prism': 'PRISM — Code Property Graph Engine | Lexint',
  '/website/pricing': 'Pricing — Lexint',
  '/website/about': 'About Lexint',
  '/website/faq': 'FAQ — Lexint',
  '/website/knowledge': 'Knowledge Hub — Lexint',
  '/website/eu-cra': 'EU CRA Compliance — Lexint',
  '/website/integrations': 'Integrations — Lexint',
  '/website/contact': 'Contact — Lexint',
  '/website/security': 'Security & Trust — Lexint',
  '/website/privacy': 'Privacy Policy — Lexint',
  '/website/terms': 'Terms of Service — Lexint',
};

function useSeoTitle() {
  const location = useLocation();
  useEffect(() => {
    const title = PAGE_TITLES[location.pathname] || 'Lexint — Code Intelligence Platform';
    document.title = title;
  }, [location.pathname]);
}

/* ── Cookie consent banner ─────────────────────────────────── */
function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('lexint_cookie_consent');
    if (!consent) setVisible(true);
  }, []);

  function accept() {
    localStorage.setItem('lexint_cookie_consent', 'all');
    setVisible(false);
  }
  function essential() {
    localStorage.setItem('lexint_cookie_consent', 'essential');
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999,
      background: '#1e293b', borderTop: '1px solid rgba(255,255,255,.08)',
      padding: '16px 24px', display: 'flex', alignItems: 'center',
      gap: 20, flexWrap: 'wrap', justifyContent: 'space-between',
      boxShadow: '0 -8px 32px rgba(0,0,0,.4)',
      animation: 'slideUpBanner .3s ease',
    }}>
      <div style={{flex: 1, minWidth: 280}}>
        <p style={{margin: 0, fontSize: 13.5, color: '#e2e8f0', lineHeight: 1.5}}>
          🍪 We use essential cookies to operate this website and optional analytics to improve it.
          We never sell your data or use advertising trackers.{' '}
          <Link to="/website/privacy" style={{color: '#a5b4fc', textDecoration: 'underline'}}>
            Privacy Policy
          </Link>
        </p>
      </div>
      <div style={{display: 'flex', gap: 10, flexShrink: 0}}>
        <button onClick={essential} style={{
          padding: '9px 18px', borderRadius: 8, border: '1px solid rgba(255,255,255,.15)',
          background: 'transparent', color: '#94a3b8', cursor: 'pointer',
          fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
        }}>
          Essential only
        </button>
        <button onClick={accept} style={{
          padding: '9px 18px', borderRadius: 8, border: 'none',
          background: '#6366f1', color: '#fff', cursor: 'pointer',
          fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
        }}>
          Accept all
        </button>
      </div>
    </div>
  );
}

function navCls({ isActive }) {
  return `dgw-nav-link${isActive ? ' active' : ''}`;
}

const PRODUCTS = [
  { to: '/website/product/depguard', cls: 'depguard', icon: '🛡️', name: 'DepGuard', desc: 'SBOM & Supply Chain Security' },
  { to: '/website/product/vigil',    cls: 'vigil',    icon: '👁️', name: 'Vigil',    desc: 'Open-Source SAST Engine' },
  { to: '/website/product/prism',    cls: 'prism',    icon: '◈',  name: 'Prism',    desc: 'Code Property Graph Engine' },
];

export default function WebsiteLayout() {
  useSeoTitle();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  // Close drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Prevent body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <div className="dgw-shell">
      <header className="dgw-topbar">
        <div className="dgw-topbar-inner">
          {/* Brand */}
          <Link className="dgw-brand" to="/website">
            <span className="dgw-brand-icon">Lx</span>
            <span className="dgw-brand-copy">
              <strong>Lexint</strong>
              <small>Code Intelligence Platform</small>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="dgw-nav" aria-label="Main navigation">
            {/* Products dropdown */}
            <div className="dgw-nav-products">
              <button className="dgw-nav-products-btn">
                Products
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <div className="dgw-nav-products-dropdown">
                {PRODUCTS.map(p => (
                  <Link key={p.to} to={p.to} className="dgw-dropdown-item">
                    <span className={`dgw-dropdown-icon ${p.cls}`}>{p.icon}</span>
                    <span>
                      <div className="dgw-dropdown-name">{p.name}</div>
                      <div className="dgw-dropdown-desc">{p.desc}</div>
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            <NavLink to="/website/integrations" className={navCls}>Integrations</NavLink>
            <NavLink to="/website/pricing"      className={navCls}>Pricing</NavLink>
            <NavLink to="/website/eu-cra"       className={navCls}>EU CRA</NavLink>
            <NavLink to="/website/about"        className={navCls}>About</NavLink>
          </nav>

          {/* Desktop Actions */}
          <div className="dgw-nav-actions">
            <Link className="dgw-btn dgw-btn-ghost" to="/login">Sign in</Link>
            <Link className="dgw-btn dgw-btn-primary" to="/trial?plan=professional">Start free →</Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className={`dgw-hamburger${mobileOpen ? ' open' : ''}`}
            onClick={() => setMobileOpen(v => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            <span /><span /><span />
          </button>
        </div>
      </header>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="dgw-mobile-overlay" onClick={() => setMobileOpen(false)} aria-hidden="true" />
      )}

      {/* Mobile drawer */}
      <nav className={`dgw-mobile-menu${mobileOpen ? ' open' : ''}`} aria-label="Mobile navigation">
        <div className="dgw-mobile-menu-header">
          <Link className="dgw-brand" to="/website">
            <span className="dgw-brand-icon">Lx</span>
            <span className="dgw-brand-copy">
              <strong>Lexint</strong>
              <small>Code Intelligence Platform</small>
            </span>
          </Link>
          <button className="dgw-mobile-close" onClick={() => setMobileOpen(false)} aria-label="Close menu">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M2 2l14 14M16 2L2 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="dgw-mobile-section-label">Products</div>
        {PRODUCTS.map(p => (
          <Link key={p.to} to={p.to} className="dgw-mobile-product-link">
            <span className={`dgw-dropdown-icon ${p.cls}`}>{p.icon}</span>
            <span>
              <div className="dgw-mobile-product-name">{p.name}</div>
              <div className="dgw-mobile-product-desc">{p.desc}</div>
            </span>
          </Link>
        ))}

        <div className="dgw-mobile-divider" />
        <div className="dgw-mobile-section-label">Navigation</div>

        <NavLink to="/website/integrations" className={navCls}>Integrations</NavLink>
        <NavLink to="/website/pricing"      className={navCls}>Pricing</NavLink>
        <NavLink to="/website/eu-cra"       className={navCls}>EU CRA</NavLink>
        <NavLink to="/website/about"        className={navCls}>About</NavLink>
        <NavLink to="/website/faq"          className={navCls}>FAQ</NavLink>
        <NavLink to="/website/security"     className={navCls}>Security</NavLink>

        <div className="dgw-mobile-divider" />
        <div className="dgw-mobile-actions">
          <Link className="dgw-btn dgw-btn-ghost" to="/login" style={{justifyContent:'center'}}>Sign in</Link>
          <Link className="dgw-btn dgw-btn-primary" to="/trial?plan=professional" style={{justifyContent:'center'}}>Start free →</Link>
        </div>
      </nav>

      <main className="dgw-content">
        <Outlet />
      </main>

      <CookieBanner />

      <footer className="dgw-footer">
        <div className="dgw-footer-inner">
          {/* Brand column */}
          <div className="dgw-footer-brand">
            <span className="dgw-brand-icon" style={{display:'inline-flex',alignItems:'center',justifyContent:'center',width:36,height:36,borderRadius:10,background:'linear-gradient(135deg,#6366f1,#8b5cf6)',color:'#fff',fontWeight:800,fontSize:14}}>Lx</span>
            <strong>Lexint</strong>
            <p>The Code Intelligence Platform. One graph. Three products. Zero fragmentation.</p>
            <div className="dgw-footer-actions">
              <Link className="dgw-btn dgw-btn-primary" to="/trial?plan=professional">Start free</Link>
              <Link className="dgw-btn dgw-btn-dark" to="/contact?type=enterprise">Talk to sales</Link>
            </div>
          </div>

          {/* Link columns */}
          <div className="dgw-footer-columns">
            <div className="dgw-footer-column">
              <span className="dgw-footer-col-label">Products</span>
              <div className="dgw-footer-links">
                <Link to="/website/product/depguard">DepGuard</Link>
                <Link to="/website/product/vigil">Vigil</Link>
                <Link to="/website/product/prism">Prism</Link>
                <Link to="/website/pricing">Pricing</Link>
              </div>
            </div>
            <div className="dgw-footer-column">
              <span className="dgw-footer-col-label">Solutions</span>
              <div className="dgw-footer-links">
                <Link to="/website/eu-cra">EU CRA Compliance</Link>
                <Link to="/website/integrations">Integrations</Link>
                <Link to="/website/security">Security & Trust</Link>
                <Link to="/website/whitepaper">Whitepaper</Link>
              </div>
            </div>
            <div className="dgw-footer-column">
              <span className="dgw-footer-col-label">Resources</span>
              <div className="dgw-footer-links">
                <Link to="/website/knowledge">Knowledge Hub</Link>
                <Link to="/website/faq">FAQ</Link>
                <Link to="/login">Portal sign-in</Link>
                <Link to="/website/about">About Lexint</Link>
              </div>
            </div>
            <div className="dgw-footer-column">
              <span className="dgw-footer-col-label">Company</span>
              <div className="dgw-footer-links">
                <Link to="/website/contact">Contact</Link>
                <Link to="/contact?type=demo">Book a demo</Link>
                <Link to="/contact?type=enterprise">Enterprise</Link>
                <Link to="/trial?plan=professional">Free trial</Link>
              </div>
            </div>
          </div>
        </div>

        <div className="dgw-footer-bottom">
          <span>© 2026 Lexint. All rights reserved.</span>
          <div style={{display:'flex', gap:20, alignItems:'center', flexWrap:'wrap', justifyContent:'center'}}>
            <Link to="/website/privacy" style={{color:'#64748b', textDecoration:'none', fontSize:13}}>Privacy Policy</Link>
            <Link to="/website/terms" style={{color:'#64748b', textDecoration:'none', fontSize:13}}>Terms of Service</Link>
            <Link to="/website/security" style={{color:'#64748b', textDecoration:'none', fontSize:13}}>Security</Link>
            <a href="https://github.com/lexint" target="_blank" rel="noopener noreferrer" style={{color:'#64748b', textDecoration:'none', fontSize:13}}>GitHub</a>
            <span style={{color:'#334155', fontSize:13}}>Built for the EU Cyber Resilience Act era.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
